<!--
  - Copyright (c) 2021 by Leyton Group - All Rights Reserved.
  -  This source code, and resulting software, is the confidential and proprietary information
  -  ("Proprietary Information") and is the intellectual property ("Intellectual Property")
  -  of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
  -  and shall use it only in accordance with the terms and conditions of any and all license
  -  agreements you have entered into with The Company.
  -->

<template>
  <div id="WorksList">
    <div>
      <actions
        :view="view"
        v-on:changeView="changeView($event)"
        :queryConfig="queryConfig"
        @applyFilter="applyFilter($event)"
        @reloadData="applyFilter($event)"
        @download="download($event)"
        @search="workSearch"
      ></actions>

      <page-loader v-if="loading" :hasActions="true"></page-loader>

      <!-- EMPTY STATE -->
      <empty-state
        v-if="!getWorks.content.length"
        module="ouvrages"
        title="Commencer à estimer votre projet."
        description="Vous n'avez aucun ouvrage."
        :addSimulation="true"
      ></empty-state>

      <div v-if="getWorks.content.length" class="giga-ouvrages_content">
        <div v-if="view == 'grid' && !loading">
          <work-grid :works="getWorks.content"></work-grid>
        </div>

        <div v-if="view == 'list' && !loading">
          <table class="giga-table" :aria-describedby="$t('all_works')">
            <thead>
              <tr>
                <th
                  @click="sortTable('intitule')"
                  class="sort"
                  scope="col"
                  :class="
                    sortBy == 'intitule' ? (sortDir ? 'desc' : 'asc') : ''
                  "
                >
                  {{ $t("name_work") }}
                </th>
                <th scope="col" class="empty-column"></th>
                <th scope="col">
                  {{ $t("precarious_volume") }}
                </th>
                <th scope="col">
                  {{ $t("classic_volume") }}
                </th>
                <th scope="col">
                  {{ $t("economic_valorisation") }}
                </th>
                <th scope="col">
                  {{ $t("number_of_operations") }}
                </th>
                <th scope="col" class="giga-status_col">
                  {{ $t("status") }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="work in getWorks.content">
                <tr
                  class="cursor-pointer"
                  @click="onRowSelected(work.id)"
                  :key="work.id"
                >
                  <td>
                    <p>{{ work.name }}</p>
                  </td>
                  <td class="empty-column">
                    <div class="icon-group">
                      <span
                        class="tooltip-container"
                        v-if="
                          work.cofracState === 'BOTH' ||
                            work.cofracState === 'ONLY_COFRAC'
                        "
                      >
                        <svg
                          class="warning-icon"
                          viewBox="0 0 15 15"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            class="triangle"
                            d="M7.5 1.2 C7 1.2 6.7 1.5 6.5 1.8 L0.8 11.2
                                   A1.5 1.5 0 0 0 2.2 13 H12.8 A1.5 1.5 0 0 0 14.2 11.2
                                   L8.5 1.8 C8.3 1.5 8 1.2 7.5 1.2Z"
                          />
                          <path
                            class="exclamation-mark"
                            d="M7.5 3.5C7.75 3.5 8 3.75 8 4L7.8 8C7.8 8.25 7.6 8.5 7.5 8.5
                                   C7.4 8.5 7.2 8.25 7.2 8L7 4C7 3.75 7.25 3.5 7.5 3.5Z"
                          />
                          <circle
                            class="exclamation-mark"
                            cx="7.5"
                            cy="10.5"
                            r="0.8"
                          />
                        </svg>
                        <span class="tooltip-text">{{
                          $t("quality_control_required")
                        }}</span>
                      </span>

                      <span
                        class="tooltip-container"
                        v-if="work.isRgeInstaller"
                      >
                        <img
                          src="@/assets/images/rge.png"
                          alt="RGE"
                          class="rge-icon"
                        />
                        <span class="tooltip-text">{{
                          $t("rge_installer_required")
                        }}</span>
                      </span>
                    </div>
                  </td>
                  <td>
                    <p>
                      {{ work.precarityVolume | formatNumber
                      }}<span> kWh cumac</span>
                    </p>
                  </td>
                  <td>
                    <p>
                      {{ work.classicVolume | formatNumber
                      }}<span> kWh cumac</span>
                    </p>
                  </td>
                  <td>
                    <p>
                      <span>€ </span
                      >{{ work.economicValorisation | formatNumber }}
                    </p>
                  </td>
                  <td>
                    <p>{{ work.numberOfOperations }}</p>
                  </td>
                  <td>
                    <b-badge
                      class="giga-badge"
                      :class="[
                        { inProgress: work.status == 'IN_PROGRESS' },
                        { delivered: work.status == 'DELIVERED' },
                        { deposited: work.status == 'DEPOSITED' }
                      ]"
                    >
                      {{
                        work.advancedStatus
                          ? $t(`operation_status.${work.advancedStatus}`)
                          : $t(work.status)
                      }}
                    </b-badge>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <pagination
          v-if="rows >= perPage"
          :length="length"
          :rows="rows"
          :perPage="perPage"
          :currentPage="currentPage"
          v-on:changePerPage="changePerPage($event)"
          v-on:changeCurrentPage="changeCurrentPage($event)"
        ></pagination>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Actions from "../../components/static/Actions.vue";
import Pagination from "../../components/static/Pagination.vue";
import WorkGrid from "../../components/static/WorkGrid.vue";
import PageLoader from "../../components/static/pageLoader.vue";
import { queryConstant } from "../../shared/queryConstant";
import { workService } from "./services";
import { debounce, cloneDeep } from "lodash";
import EmptyState from "../../components/static/emptyState.vue";
import { constants } from "@/shared/constants";
import _ from "lodash";
import { helpers } from "@/shared/helper";

export default {
  name: "WorksList",
  components: { Actions, Pagination, WorkGrid, PageLoader, EmptyState },
  data() {
    const _pageSize_10 = 10;
    const _pageSize_25 = 25;
    const _pageSize_50 = 50;
    return {
      view: "list",
      scrollBarSettings: {
        scrollXMarginOffset: 50
      },
      perPage: _pageSize_10,
      currentPage: 1,
      length: [_pageSize_10, _pageSize_25, _pageSize_50],
      sortBy: "id",
      sortDir: true,
      customFilter: queryConstant.setDefaultCustomFilter(),
      search: "",
      queryConfig: queryConstant.WORK_CONFIG,
      fetchData: () => {},
      USER_TYPE: constants.USER_TYPE,
      ROLE_TYPE: constants.ROLE_TYPE
    };
  },
  mounted() {
    this.fetchData = _.debounce(this.fetchWorks, 500);
    this.fetchWorks();
    if (!this.sidebarShowStatus) {
      this.view = "grid";
      this.setSidebarShow(true);
      this.toggleSidebar();
    }
    const otherQueryConfig = cloneDeep(queryConstant.WORK_CONFIG);
    if (
      this.currentUser.clientType === this.USER_TYPE.B2B2B &&
      this.currentUser.admin
    ) {
      otherQueryConfig.fields.push(
        queryConstant.AGENCY_FILTER_OPTION,
        queryConstant.USER_FILTER_OPTION
      );
    } else if (
      this.currentUser.clientType === this.USER_TYPE.B2B2B &&
      this.currentUser.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE
    ) {
      otherQueryConfig.fields.push(
        queryConstant.AGENCY_FILTER_OPTION,
        queryConstant.USER_FILTER_OPTION
      );
    } else if (this.isNotB2B2B && this.currentUser.admin) {
      otherQueryConfig.fields.push(queryConstant.USER_FILTER_OPTION);
    }
    this.queryConfig = otherQueryConfig;
  },
  computed: {
    ...mapGetters("account", { currentUser: "getCurrentUser" }),
    ...mapGetters("user", ["getUsers"]),
    ...mapGetters("work", ["getWorks"]),
    ...mapGetters("loader", { loading: "getLoading" }),
    ...mapGetters("leftsidebar", { sidebarShowStatus: "getSidebarShowStatus" }),
    rows() {
      return this.getWorks.totalElements;
    },
    isAnyOfUserTypes() {
      return helpers.isAnyOfUserTypes();
    },
    isNotB2B2B() {
      return helpers.isNotB2B2B();
    }
  },
  methods: {
    ...mapActions("work", [
      "getWorksAction",
      "getWorksAdminAgence",
      "getWorksChargeDeProjet",
      "getWorksChargeDeProjetById"
    ]),
    ...mapMutations({
      toggleSidebar: "leftsidebar/toggleSidebar",
      setSidebarShow: "leftsidebar/setSidebarShow"
    }),
    ...mapMutations("work", ["setWorks"]),
    workSearch(search) {
      console.log("workSearch", search);
      this.search = search;
      this.fetchData();
      // _.debounce(this.fetchWorks, 500)();
      // this.fetchWorks();
    },
    changePerPage(perPage) {
      this.currentPage = 1;
      this.perPage = perPage;
      this.fetchWorks();
    },
    changeCurrentPage(currentPage) {
      this.currentPage = currentPage;
      this.fetchWorks();
    },
    changeView(view) {
      this.view = view;
    },
    sortTable(name) {
      if (name === this.sortBy) {
        this.sortDir = !this.sortDir;
      } else {
        this.sortDir = false;
      }
      this.sortBy = name;
      this.fetchWorks();
    },
    onRowSelected(id) {
      this.$router.push({ name: "WorkDetail", params: { id: id } });
    },
    applyFilter(event) {
      this.customFilter = event.filter.custom;
      this.fetchWorks();
    },
    fetchWorks() {
      if (
        this.currentUser.clientType === this.USER_TYPE.B2B2B &&
        this.customFilter.rules.some(rule => rule.field === "agence") &&
        (this.currentUser.admin ||
          this.currentUser.role?.roleType === this.ROLE_TYPE.ADMIN_AGENCE)
      ) {
        const agence = this.customFilter.rules.find(
          rule => rule.field === "agence"
        );
        let customFilter = cloneDeep(this.customFilter);
        customFilter.rules = this.customFilter.rules.filter(
          rule => rule.field !== "agence"
        );
        this.getWorksAdminAgence({
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          customFilter: customFilter,
          search: this.search,
          agenceId: agence?.rules[0]?.value?.id
        });
      } else if (
        this.isAnyOfUserTypes &&
        this.customFilter.rules.some(rule => rule.field === "user")
      ) {
        const user = this.customFilter.rules.find(
          rule => rule.field === "user"
        );
        let customFilter = cloneDeep(this.customFilter);
        customFilter.rules = this.customFilter.rules.filter(
          rule => rule.field !== "user"
        );
        this.getWorksChargeDeProjetById({
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          customFilter: customFilter,
          search: this.search,
          userId: user?.rules[0]?.value?.id
        });
      } else if (
        (this.currentUser.clientType === this.USER_TYPE.B2B2B &&
          this.currentUser.role?.roleType === this.ROLE_TYPE.USER) ||
        (this.isNotB2B2B &&
          this.currentUser.role?.roleType === this.ROLE_TYPE.USER)
      ) {
        this.getWorksChargeDeProjet({
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          customFilter: this.customFilter,
          search: this.search
        });
      } else {
        this.getWorksAction({
          currentPage: this.currentPage,
          perPage: this.perPage,
          sortBy: this.sortBy,
          sortDir: this.sortDir,
          customFilter: this.customFilter,
          search: this.search
        });
      }
    },
    download() {
      let customFilter = cloneDeep(this.customFilter);
      customFilter.rules
        .filter(rule => rule.field === "agence")
        .forEach(rule => {
          if (rule.rules?.[0]?.value?.id) {
            rule.rules[0].value = rule.rules[0].value.id;
          }
        });
      workService.downloadWorks(customFilter);
    }
  }
};
</script>

<style lang="scss">
.view-area {
  position: relative;
  min-height: 560px;
  height: calc(100vh - 480px);
  padding-right: 1rem;
  margin-right: -1rem;
  overflow-y: auto;
}
.view-area-table {
  position: relative;
  min-height: 530px;
  height: calc(100vh - 530px);
  padding-right: 20px;
}
.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip-container .tooltip-text {
  visibility: hidden;
  width: auto;
  white-space: nowrap;
  background: #fffc9d;
  color: black;
  text-align: center;
  padding: 6px;
  border-radius: 6px;
  position: absolute;
  z-index: 10;
  top: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
  font-size: 12px;
}
.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}
.warning-icon,
.rge-icon {
  width: 22px;
  height: 22px;
  margin-left: 6px;
  vertical-align: middle;
  cursor: pointer;
}
.triangle {
  fill: #fffc9d;
  stroke: black;
  stroke-width: 1px;
  stroke-linejoin: round;
}
.exclamation-mark {
  fill: black;
}
.empty-column {
  width: 60px !important;
  padding: 0;
}
.icon-group {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  position: relative;
}
</style>
